'use client';

import Image from '@/components/Image';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { setSession } from '@/utils/tools';
import Link from 'next/link';
import { useCallback, useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { Autoplay } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useTransitionTracker } from '../hooks/useTransitionTracker';

interface SeriesItem {
  img: string;
  title: string;
  description: string;
  bottom_image: string;
  group_id: number;
}

interface SeriesOverviewProps {
  seriesOverviewData: {
    title_img: string;
    title: string;
    aspectRatio?: string;
    series_arr: SeriesItem[];
  };
}

// 创建一个单独的组件来处理每个系列项
const SeriesItemComponent = ({
  item,
  index,
  onExpose,
  onClick,
  aspectRatio,
  isPCVersion = false,
}: {
  item: SeriesItem;
  index: number;
  onExpose: (item: SeriesItem, index: number) => void;
  onClick: (item: SeriesItem, index: number) => void;
  aspectRatio?: string;
  isPCVersion?: boolean;
}) => {
  const { ref, inView } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView) {
      onExpose(item, index);
    }
  }, [inView, item, index, onExpose]);

  if (isPCVersion) {
    return (
      <Link
        key={index}
        ref={ref}
        href={`/goods-list/${item.group_id}`}
        className="block cursor-pointer flex-1 max-w-[270px] md:max-w-[320px] lg:max-w-[420px] 2xl:max-w-[540px]"
        style={{ aspectRatio: aspectRatio || '540/620' }}
        onClick={(e) => {
          e.preventDefault();
          setSession('zl_goodsListEnterPageScene', '活动专题页');
          onClick(item, index);
        }}
        aria-label={item.title}
      >
        {/* 信息包装器 */}
        <div className="relative">
          <Image
            src={item.img}
            alt={item.title}
            width={540}
            height={460}
            className="w-full h-auto"
          />
          {/* 文字盒子 */}
          <div className="absolute bottom-[12px] px-[15px] text-white md:bottom-[16px] md:px-[20px] lg:bottom-[20px] lg:px-[25px] 2xl:bottom-[24px] 2xl:px-[30px]">
            <h3 className="w-full font-bb mb-[5px] text-[18px] leading-[21px] md:text-[24px] md:leading-[28px] md:mb-[6px] lg:text-[30px] lg:leading-[35px] lg:mb-[8px] 2xl:text-[36px] 2xl:leading-[42px] 2xl:mb-[10px] text-ellipsis line-clamp-3">
              {item.title}
            </h3>
            <p className="w-full font-br text-[12px] leading-[15px] md:text-[14px] md:leading-[18px] lg:text-[16px] lg:leading-[22px] 2xl:text-[18px] 2xl:leading-[26px] text-ellipsis line-clamp-3">
              {item.description}
            </p>
          </div>
        </div>

        {/* 图片包装器 */}
        <div className="flex justify-center items-center w-full bg-white h-[80px] md:h-[100px] lg:h-[130px] 2xl:h-[160px]">
          <Image
            src={item.bottom_image}
            alt=""
            width={200}
            height={110}
            className="w-auto h-[55px] md:h-[70px] lg:h-[90px] 2xl:h-[110px]"
          />
        </div>
      </Link>
    );
  }

  return (
    <div ref={ref} className="w-full h-full" data-index={index}>
      {/* 信息包装器 */}
      <div className="relative" data-index={index}>
        <Image
          src={item.img}
          alt={item.title}
          width={270}
          height={200}
          className="w-full h-auto"
          data-index={index}
        />
        {/* 文字盒子 */}
        <div className="absolute bottom-[10px] px-[15px] text-white" data-index={index}>
          <h3
            className="w-full font-bb text-[18px] leading-[21px] mb-[5px] text-ellipsis line-clamp-3"
            data-index={index}
          >
            {item.title}
          </h3>
          <p
            className="w-full font-br text-[12px] leading-[15px] text-ellipsis line-clamp-3"
            data-index={index}
          >
            {item.description}
          </p>
        </div>
      </div>

      {/* 图片包装器 */}
      <div className="flex justify-center items-center w-full h-[80px] bg-white" data-index={index}>
        <Image
          src={item.bottom_image}
          alt=""
          width={100}
          height={55}
          className="h-[55px] w-auto"
          data-index={index}
        />
      </div>
    </div>
  );
};

export default function SeriesOverview({ seriesOverviewData }: SeriesOverviewProps) {
  const { navigate } = useAppNavigation();
  const { trackOverviewExp, trackOverviewClick } = useTransitionTracker();

  const handleItemClick = useCallback(
    (item: SeriesItem, index: number) => {
      setSession('zl_goodsListEnterPageScene', '活动专题页');
      navigate(`/goods-list/${item.group_id}`);

      // 点击埋点
      trackOverviewClick({
        pits_index: index,
        group_id: `${item.group_id}`,
      });
    },
    [navigate, trackOverviewClick],
  );

  const handleItemExpose = useCallback(
    (item: SeriesItem, index: number) => {
      trackOverviewExp({
        pits_index: index,
        group_id: `${item.group_id}`,
      });
    },
    [trackOverviewExp],
  );

  const handleMobileClick = useCallback(
    (e: React.MouseEvent) => {
      try {
        const target = e.target as HTMLElement;
        const indexAttr =
          target.getAttribute('data-index') ||
          target.closest('[data-index]')?.getAttribute('data-index');

        if (indexAttr !== null) {
          const index = parseInt(indexAttr, 10);
          const item = seriesOverviewData.series_arr[index];
          if (item?.group_id) {
            handleItemClick(item, index);
          }
        }
      } catch {
        // 静默处理错误，避免中断用户操作
      }
    },
    [seriesOverviewData.series_arr, handleItemClick],
  );

  // 使用 useMemo 优化 Swiper 配置
  const swiperOptions = useMemo(() => {
    return seriesOverviewData.series_arr.length > 2
      ? {
          observer: true,
          observeParents: true,
          observeSlideChildren: true,
          slidesPerView: 'auto' as const,
          slidesPerGroup: 1,
          spaceBetween: 14,
          slidesOffsetBefore: 14,
          slidesOffsetAfter: 14,
          loop: true,
          autoplay: {
            delay: 5000,
            stopOnLastSlide: false,
            pauseOnMouseEnter: true,
            disableOnInteraction: false,
          },
        }
      : {
          observer: true,
          observeParents: true,
          observeSlideChildren: true,
          spaceBetween: 14,
          slidesOffsetBefore: 14,
          slidesPerView: 'auto' as const,
          slidesOffsetAfter: 14,
        };
  }, [seriesOverviewData.series_arr.length]);

  if (!seriesOverviewData?.series_arr?.length) {
    return null;
  }

  const PCVersion = () => (
    <div className="w-full">
      <div className="min-w-[768px] px-[60px] py-[27px] pb-[36px] md:px-[80px] md:py-[40px] md:pb-[50px] lg:px-[100px] lg:py-[50px] lg:pb-[60px] 2xl:px-[120px] 2xl:py-[54px] 2xl:pb-[72px]">
        <div className="text-center mb-[25px] md:mb-[35px] lg:mb-[40px] 2xl:mb-[50px]">
          {seriesOverviewData.title_img && (
            <Image
              src={seriesOverviewData.title_img}
              alt={seriesOverviewData.title}
              width={360}
              height={101}
              className="w-auto mx-auto h-[50px] md:h-[70px] lg:h-[85px] 2xl:h-[101px]"
            />
          )}
          <h2 className="text-primary-black text-[18px] md:text-[20px] lg:text-[22px] 2xl:text-[24px]">
            {seriesOverviewData.title}
          </h2>
        </div>

        <div className="flex justify-between items-center gap-[16px] lg:gap-[20px] 2xl:gap-[24px]">
          {seriesOverviewData.series_arr.map((item, index) => (
            <SeriesItemComponent
              key={index}
              item={item}
              index={index}
              onExpose={handleItemExpose}
              onClick={handleItemClick}
              aspectRatio={seriesOverviewData.aspectRatio}
              isPCVersion
            />
          ))}
        </div>
      </div>
    </div>
  );

  const MobileVersion = () => (
    <div
      className="w-full box-border"
      style={{ aspectRatio: seriesOverviewData.aspectRatio || '375/472' }}
      onClick={handleMobileClick}
    >
      <div className="text-center mb-[20px] pt-[27px]">
        {seriesOverviewData.title_img && (
          <div>
            <Image
              src={seriesOverviewData.title_img}
              alt={seriesOverviewData.title}
              width={240}
              height={51}
              className="h-[51px] w-auto mx-auto"
            />
          </div>
        )}
        <h2 className="text-[12px] text-primary-black">{seriesOverviewData.title}</h2>
      </div>

      {seriesOverviewData.series_arr.length > 0 && (
        <Swiper
          {...swiperOptions}
          modules={seriesOverviewData.series_arr.length > 2 ? [Autoplay] : []}
          className="flex justify-between items-center pb-[46px]"
        >
          {seriesOverviewData.series_arr.map((item, index) => (
            <SwiperSlide key={index} className="!w-[270px]" data-index={index}>
              <SeriesItemComponent
                item={item}
                index={index}
                onExpose={handleItemExpose}
                onClick={handleItemClick}
                aspectRatio={seriesOverviewData.aspectRatio}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      )}
    </div>
  );

  return (
    <section className="w-full">
      <WebOnly>
        <PCVersion />
      </WebOnly>
      <WapOnly>
        <MobileVersion />
      </WapOnly>
    </section>
  );
}
